import { useState } from "react";
import {
  Box, Typography, Paper, Stack, Button, Chip, Alert, Divider,
  Table, TableHead, TableRow, TableCell, TableBody, Grid2 as Grid,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CheckIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/CancelOutlined";
import BlockIcon from "@mui/icons-material/Block";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { purchaseOrdersApi, purchaseOrderStateApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

const STATUS_COLOR: Record<string, "default" | "warning" | "info" | "success" | "error"> = {
  Draft: "default", Submitted: "warning", Approved: "info", Posted: "success", Cancelled: "error",
};

type ActionKind = null | "submit" | "approve" | "reject" | "cancel";

export function PurchaseOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [action, setAction] = useState<ActionKind>(null);
  const [comment, setComment] = useState("");

  const canApprove = useAuth(s => s.hasPermission("procurement.po.approve"));
  const canEdit = useAuth(s => s.hasPermission("procurement.po.create"));
  const canCreateGrn = useAuth(s => s.hasPermission("procurement.grn.create"));

  const query = useQuery({
    queryKey: ["purchase-order", id],
    queryFn: () => purchaseOrdersApi.get(id!),
    enabled: !!id,
  });

  const mutation = useMutation({
    mutationFn: async () => {
      if (!id) return;
      switch (action) {
        case "submit":  await purchaseOrderStateApi.submit(id, comment || undefined); break;
        case "approve": await purchaseOrderStateApi.approve(id, comment || undefined); break;
        case "reject":  await purchaseOrderStateApi.reject(id, comment); break;
        case "cancel":  await purchaseOrderStateApi.cancel(id, comment); break;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["purchase-order", id] });
      qc.invalidateQueries({ queryKey: ["purchase-orders"] });
      setAction(null); setComment("");
    },
  });

  const openAction = (a: Exclude<ActionKind, null>) => { setAction(a); setComment(""); };

  if (query.isLoading) return <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress /></Stack>;
  if (query.isError) return <Alert severity="error">{errorMessage(query.error)}</Alert>;
  if (!query.data) return null;

  const po = query.data;
  const isDraft = po.status === "Draft";
  const isSubmitted = po.status === "Submitted";
  const isApproved = po.status === "Approved";
  const isTerminal = po.status === "Posted" || po.status === "Cancelled";

  const needsReason = action === "reject" || action === "cancel";
  const submittable = !needsReason || comment.trim().length > 0;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/procurement/purchase-orders")}>
          All POs
        </Button>
        <Typography variant="h5" fontWeight={600}>{po.documentNo}</Typography>
        <Chip label={po.status} color={STATUS_COLOR[po.status] ?? "default"} />
        <Box sx={{ flex: 1 }} />
        {canEdit && isDraft && (
          <Button variant="contained" startIcon={<SendIcon />} onClick={() => openAction("submit")}>
            Submit
          </Button>
        )}
        {canApprove && isSubmitted && (
          <>
            <Button color="success" variant="contained" startIcon={<CheckIcon />} onClick={() => openAction("approve")}>
              Approve
            </Button>
            <Button color="warning" variant="outlined" startIcon={<BlockIcon />} onClick={() => openAction("reject")}>
              Reject
            </Button>
          </>
        )}
        {canEdit && !isTerminal && (
          <Button color="error" variant="outlined" startIcon={<CancelIcon />} onClick={() => openAction("cancel")}>
            Cancel
          </Button>
        )}
        {canCreateGrn && isApproved && (
          <Button variant="contained" color="primary" startIcon={<MoveToInboxIcon />}
            onClick={() => navigate(`/procurement/grns/new?poId=${po.id}`)}>
            Create GRN
          </Button>
        )}
      </Stack>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="caption" color="text.secondary">Vendor</Typography>
            <Typography>{po.vendorName}</Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Typography variant="caption" color="text.secondary">Document date</Typography>
            <Typography>{new Date(po.documentDate).toLocaleDateString()}</Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Typography variant="caption" color="text.secondary">Expected delivery</Typography>
            <Typography>{po.expectedDeliveryDate ? new Date(po.expectedDeliveryDate).toLocaleDateString() : "—"}</Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Typography variant="caption" color="text.secondary">Vendor ref</Typography>
            <Typography>{po.vendorRef ?? "—"}</Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Typography variant="caption" color="text.secondary">Total</Typography>
            <Typography variant="h6" color="primary">{inr.format(po.totalAmount)}</Typography>
          </Grid>
          {po.notes && (
            <Grid size={12}>
              <Typography variant="caption" color="text.secondary">Notes</Typography>
              <Typography sx={{ whiteSpace: "pre-line" }}>{po.notes}</Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Lines</Typography>

        {/* UC-M1-PROC-02: PO source banner. Tells the user where the lines came from. */}
        {(() => {
          const sources = Array.from(new Set(po.lines.map(l => (l as any).source as string)));
          if (sources.length === 0) return null;
          const label =
            sources.length === 1
              ? sources[0] === "ReorderAlert" ? "Generated from reorder alert"
                : sources[0] === "Requisition" ? "Generated from purchase indent"
                : sources[0] === "Schedule" ? "Scheduled replenishment (cron)"
                : sources[0] === "Quote" ? "Converted from vendor quotation"
                : "Manual PO"
              : "Mixed sources";
          return (
            <Alert severity="info" sx={{ mb: 2 }}>
              <b>Source:</b> {label}
            </Alert>
          );
        })()}

        <Divider sx={{ mb: 1 }} />
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Rate</TableCell>
              <TableCell align="right">Tax</TableCell>
              <TableCell align="right">Line total</TableCell>
              <TableCell align="right">Received</TableCell>
              <TableCell align="right">Pending</TableCell>
              <TableCell align="right">Reorder hint</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {po.lines.map(l => {
              const suggested = (l as any).suggestedQty as number | undefined;
              const stockAtOrder = (l as any).stockAtOrder as number | undefined;
              const source = (l as any).source as string | undefined;
              return (
                <TableRow key={l.id}>
                  <TableCell>
                    <Stack><strong>{l.sku}</strong><span>{l.itemName}</span></Stack>
                  </TableCell>
                  <TableCell align="right">{l.qty}</TableCell>
                  <TableCell align="right">{inr.format(l.rate)}</TableCell>
                  <TableCell align="right">{inr.format(l.taxAmount)}</TableCell>
                  <TableCell align="right">{inr.format(l.lineTotal)}</TableCell>
                  <TableCell align="right">{l.receivedQty}</TableCell>
                  <TableCell align="right">
                    <Chip size="small" label={l.pendingQty}
                      color={l.pendingQty > 0 ? "warning" : "success"} variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    <Stack spacing={0.5} alignItems="flex-end">
                      {suggested != null && (
                        <Chip size="small" color="primary"
                          label={`Suggested ${suggested}`} />
                      )}
                      {stockAtOrder != null && (
                        <Chip size="small" variant="outlined"
                          color={stockAtOrder <= 0 ? "error" : "default"}
                          label={stockAtOrder <= 0 ? `OOS at order` : `Stock @ order: ${stockAtOrder}`} />
                      )}
                      {source && source !== "Manual" && (
                        <Chip size="small" variant="outlined" label={source} />
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={!!action} onClose={() => setAction(null)} maxWidth="xs" fullWidth>
        <form onSubmit={(e) => { e.preventDefault(); if (submittable) mutation.mutate(); }}>
          <DialogTitle>
            {action === "submit" && "Submit purchase order"}
            {action === "approve" && "Approve purchase order"}
            {action === "reject" && "Reject purchase order"}
            {action === "cancel" && "Cancel purchase order"}
          </DialogTitle>
          <DialogContent dividers>
            {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
            <TextField fullWidth multiline rows={3} autoFocus size="small"
              label={needsReason ? "Reason *" : "Comment (optional)"}
              value={comment} onChange={(e) => setComment(e.target.value)} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAction(null)}>Cancel</Button>
            <Button type="submit" variant="contained"
              color={action === "cancel" || action === "reject" ? "error" : "primary"}
              disabled={!submittable || mutation.isPending}
              startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
              Confirm
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
